import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import './SimpleTradeEditModal.css';

function SimpleTradeEditModal({ trade, product, isAddMode, onClose }) {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Show ALL CSV template fields - same for ALL products
  const getFieldsForProduct = () => {
    return [
      // Basic Info
      { name: 'symbol', label: 'Symbol', type: 'text', required: true },
      { name: 'status', label: 'Status', type: 'select', options: ['open', 'closed'], required: true },
      { name: 'asset_type', label: 'Asset Type', type: 'select', options: ['stock', 'option', 'credit_spread', 'debit_spread'], required: false },

      // Dates
      { name: 'entry_date', label: 'Entry Date', type: 'date', required: true },
      { name: 'exit_date', label: 'Exit Date', type: 'date', required: false },
      { name: 'expiry_date', label: 'Expiry Date', type: 'date', required: false },

      // Stock Fields
      { name: 'entry_price', label: 'Stock Entry Price', type: 'number', step: '0.01', required: false },
      { name: 'exit_price', label: 'Stock Exit Price', type: 'number', step: '0.01', required: false },
      { name: 'stock_profit_loss', label: 'Stock P/L ($)', type: 'number', step: '0.01', required: false },
      { name: 'stock_percent_return', label: 'Stock Return (%)', type: 'number', step: '0.01', required: false },

      // Option Fields
      { name: 'option_type', label: 'Option Type', type: 'select', options: ['call', 'put'], required: false },
      { name: 'option_data', label: 'Option Data (Description)', type: 'text', required: false },
      { name: 'strike_price', label: 'Strike Price', type: 'number', step: '0.01', required: false },
      { name: 'short_strike', label: 'Short Strike', type: 'number', step: '0.01', required: false },
      { name: 'long_strike', label: 'Long Strike', type: 'number', step: '0.01', required: false },
      { name: 'option_entry_price', label: 'Option Entry Price', type: 'number', step: '0.01', required: false },
      { name: 'option_exit_price', label: 'Option Exit Price', type: 'number', step: '0.01', required: false },
      { name: 'option_profit_loss', label: 'Option P/L ($)', type: 'number', step: '0.01', required: false },
      { name: 'option_percent_return', label: 'Option Return (%)', type: 'number', step: '0.01', required: false },

      // Other
      { name: 'days_held', label: 'Days Held', type: 'number', step: '1', required: false }
    ];
  };

  const fields = getFieldsForProduct();

  useEffect(() => {
    if (trade) {
      // Editing existing trade - populate all fields
      const initialData = {};
      fields.forEach(field => {
        initialData[field.name] = trade[field.name] || '';
      });
      setFormData(initialData);
    } else {
      // Adding new trade - set default values
      const initialData = {
        asset_type: 'option',  // Default to option
        status: 'open'  // Default to open
      };
      setFormData(initialData);
    }
  }, [trade]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Helper to get display labels for select options
  const getOptionLabel = (value) => {
    const labelMap = {
      'stock': 'STOCKS',
      'option': 'OPTIONS',
      'credit_spread': 'Credit Spread',
      'debit_spread': 'Debit Spread',
      'iron_condor': 'Iron Condor',
      'call': 'Call',
      'put': 'Put',
      'open': 'Open',
      'closed': 'Closed'
    };
    return labelMap[value] || value;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Prepare data - only send fields that have values
      const dataToSend = {
        product_id: product.id,
        ...formData
      };

      // For ADD mode, set required defaults
      if (isAddMode) {
        dataToSend.status = 'open';
        // asset_type is now set by the form field, don't override it

        // For ADD mode: Remove empty string values (but keep 0 and false)
        Object.keys(dataToSend).forEach(key => {
          if (dataToSend[key] === '') {
            delete dataToSend[key];
          }
        });
      } else {
        // For EDIT mode: Convert empty strings to null so backend clears the fields
        Object.keys(dataToSend).forEach(key => {
          if (dataToSend[key] === '') {
            dataToSend[key] = null;
          }
        });
      }

      if (isAddMode) {
        // Create new trade
        await api.post('/admin/trades', dataToSend);
      } else {
        // Update existing trade - backend should accept raw values without recalculation
        await api.put(`/admin/trades/${trade.id}`, dataToSend);
      }

      onClose();
    } catch (err) {
      console.error('Save error:', err);

      // Handle different error formats
      let errorMessage = 'Failed to save trade';

      if (err.response?.data) {
        const errorData = err.response.data;

        // FastAPI validation errors (array format)
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(e => `${e.loc.join('.')}: ${e.msg}`).join(', ');
        }
        // Simple string error
        else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        }
        // Generic error object
        else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }

      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content-simple" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header-simple">
          <h2>{isAddMode ? 'Add Trade' : 'Edit Trade'} - {product.name}</h2>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>

        {error && <div className="error-message-simple">{error}</div>}

        <form onSubmit={handleSubmit} className="trade-form-simple">
          <div className="form-grid-simple">
            {fields.map(field => (
              <div key={field.name} className="form-field-simple">
                <label>
                  {field.label}
                  {field.required && <span className="required">*</span>}
                </label>
                {field.type === 'select' ? (
                  <select
                    name={field.name}
                    value={formData[field.name] || ''}
                    onChange={handleChange}
                    required={field.required}
                  >
                    <option value="">Select...</option>
                    {field.options.map(opt => (
                      <option key={opt} value={opt}>{getOptionLabel(opt)}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={field.type}
                    name={field.name}
                    value={formData[field.name] || ''}
                    onChange={handleChange}
                    required={field.required}
                    step={field.step}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="modal-actions-simple">
            <button type="button" onClick={onClose} className="cancel-btn-simple" disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="save-btn-simple" disabled={loading}>
              {loading ? 'Saving...' : 'Save Trade'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SimpleTradeEditModal;
